import { Euler, Quaternion, Color } from "three";

/**
 * @description 用于控制VR设备手柄按键状态
 * @author TKB
 * @creat 2023-06-15 18:15:54
 */
const DeviceType = {
  HTC: "htc",
  PICO: "pico3",
  PICO4: "pico4",
};

const Handness = {
  LEFT: "left",
  RIGHT: "right",
};

const TouchpadDir = {
  LEFT: "left",
  RIGHT: "right",
  UP: "up",
  DOWN: "down",
};

export class VRHandleButtonControls {
  /**
   *
   * @param {*} handModel 手柄模型
   * @param {*} deviceType vr设备类型，'htc'/'pico'
   * @param {*} handness 左右手，'right'/'left'
   */
  constructor(
    handModel,
    deviceType = DeviceType.HTC,
    handness = Handness.LEFT,
    vrManager
  ) {
    this.handModel = handModel;
    this.deviceType = deviceType;
    this.handness = handness;
    this.vrManager = vrManager;

    this.triggerBtn = null; // 扳机键
    this.gripBtn = null; // 抓取键
    this.gripBtn1 = null; // htc 手柄的第二个抓取键
    this.touchpad = null; // 圆盘或摇杆
    this.axBtn = null; // a键或x键  一体机才有
    this.byBtn = null; // b键或y键  一体机才有

    this.triggerState = false;
    this.gripState = false;
    this.touchpadState = false;
    this.axState = false;
    this.byState = false;

    this._getBtns();

    this.touchpadPos = this.touchpad.position.clone();
    this.touchpadRot = this.touchpad.rotation.clone();
  }

  _getBtns() {
    if (this.handModel) {
      this.handModel.traverse((node) => {
        if (node.name === "xr_standard_trigger_pressed_value")
          this.triggerBtn = node;
        else if (node.name === "xr_standard_squeeze_pressed_value")
          this.gripBtn = node;
        else if (node.name === "xr_standard_squeeze_pressed_mirror_value")
          this.gripBtn1 = node;
        else if (
          node.name === "xr_standard_touchpad_pressed_value" ||
          node.name === "xr_standard_thumbstick_yaxis_pressed_value"
        )
          this.touchpad = node;
        else if (
          node.name === "a_button_pressed_value" ||
          node.name === "x_button_pressed_value"
        )
          this.axBtn = node;
        else if (
          node.name === "b_button_pressed_value" ||
          node.name === "y_button_pressed_value"
        )
          this.byBtn = node;
      });
    }
  }

  /**
   * 按下扳机键
   */
  triggerDown() {
    if (this.triggerBtn && !this.triggerState) {
      this.triggerBtn.rotateX(-(18 * Math.PI) / 180);
      this.triggerState = true;
    }
  }

  /**
   * 松开扳机键
   */
  triggerUp() {
    if (this.triggerBtn && this.triggerState) {
      this.triggerBtn.rotateX((18 * Math.PI) / 180);
      this.triggerState = false;
    }
  }

  /**
   * 按下抓取键
   */
  gripDown() {
    if (this.gripBtn && !this.gripState) {
      if (this.deviceType === DeviceType.PICO) {
        if (this.handness === Handness.LEFT) {
          this.gripBtn.translateX(0.002);
        } else if (this.handness === Handness.RIGHT) {
          this.gripBtn.translateX(-0.002);
        }
      } else if (this.deviceType === DeviceType.PICO4) {
        if (this.handness === Handness.LEFT) {
          this.gripBtn.translateX(-0.002);
        } else if (this.handness === Handness.RIGHT) {
          this.gripBtn.translateX(-0.002);
        }
      } else if (this.deviceType === DeviceType.HTC) {
        this.gripBtn.translateX(0.001);
      }

      if (this.gripBtn1 && this.deviceType === DeviceType.HTC) {
        this.gripBtn1.translateX(-0.001);
      }

      this.gripState = true;
    }
  }

  /**
   * 松开抓取键
   */
  gripUp() {
    if (this.gripBtn && this.gripState) {
      if (this.deviceType === DeviceType.PICO) {
        if (this.handness === Handness.LEFT) {
          this.gripBtn.translateX(-0.002);
        } else if (this.handness === Handness.RIGHT) {
          this.gripBtn.translateX(0.002);
        }
      } else if (this.deviceType === DeviceType.PICO4) {
        if (this.handness === Handness.LEFT) {
          this.gripBtn.translateX(0.002);
        } else if (this.handness === Handness.RIGHT) {
          this.gripBtn.translateX(0.002);
        }
      } else if (this.deviceType === DeviceType.HTC) {
        this.gripBtn.translateX(-0.001);
      }

      if (this.gripBtn1 && this.deviceType === DeviceType.HTC) {
        this.gripBtn1.translateX(0.001);
      }
      this.gripState = false;
    }
  }

  /**
   * 按下a键或x键
   */
  axDown() {
    if (this.axBtn && !this.axState) {
      this.axBtn.translateY(-0.001406);
      this.axState = true;
    }
  }

  /**
   * 松开a键或x键
   */
  axUp() {
    if (this.axBtn && this.axState) {
      this.axBtn.translateY(0.001406);
      this.axState = false;
    }
  }

  /**
   * 按下b键或y键
   */
  byDown() {
    if (this.byBtn && !this.byState) {
      this.byBtn.translateY(-0.001406);
      this.byState = true;
    }
  }

  /**
   * 松开b键或y键
   */
  byUp() {
    if (this.byBtn && this.byState) {
      this.byBtn.translateY(0.001406);
      this.byState = false;
    }
  }

  updateTouchpad(horizontal, vertical) {
    if (!this.touchpad) return;

    // 添加平滑过渡系数
    const SMOOTH_FACTOR = 0.5;
    // 限制最大旋转角度
    const max = Math.max(Math.abs(horizontal), Math.abs(vertical));
    const MAX_ANGLE =
      this.deviceType === DeviceType.HTC
        ? (max * 10 * Math.PI) / 180
        : (max * 20 * Math.PI) / 180;

    // 使用向量长度代替简单求和
    const magnitude = Math.sqrt(horizontal * horizontal + vertical * vertical);

    // 未按下按钮时重置
    if (magnitude <= 0.001) {
      this.touchpadUp();
      return;
    }

    // 标准化输入值
    const normalizedHorizontal = horizontal / magnitude;
    const normalizedVertical = vertical / magnitude;

    // 计算目标角度（限制最大角度）
    const targetXAngle = normalizedVertical * MAX_ANGLE;
    let targetZAngle = -normalizedHorizontal * MAX_ANGLE;

    // Pico4 右手柄需要反转Z轴角度
    if (
      this.deviceType === DeviceType.PICO4 &&
      this.handness === Handness.RIGHT
    ) {
      targetZAngle = -targetZAngle;
    }

    // 使用四元数进行平滑插值
    const currentQuaternion = this.touchpad.quaternion.clone();
    const targetEuler = new Euler(targetXAngle, 0, targetZAngle, "YXZ");
    const targetQuaternion = new Quaternion().setFromEuler(targetEuler);

    // 线性插值
    currentQuaternion.slerp(targetQuaternion, SMOOTH_FACTOR);

    // 应用旋转
    if (
      this.deviceType === DeviceType.PICO ||
      this.deviceType === DeviceType.PICO4
    ) {
      this.touchpad.quaternion.copy(currentQuaternion);
    } else if (this.deviceType === DeviceType.HTC) {
      // HTC设备添加额外的Y轴旋转
      const combinedEuler = new Euler(
        targetXAngle,
        -normalizedHorizontal * MAX_ANGLE, // 新增Y轴旋转
        targetZAngle,
        "YXZ"
      );
      this.touchpad.quaternion.setFromEuler(combinedEuler);
    }

    this.touchpadState = true;
  }

  /**
   * 圆盘或摇杆按下 废弃
   * @param {*} dir 按下方向  'left'/'right'/'up'/'down'
   */
  touchpadDown(dir = TouchpadDir.LEFT) {
    //this.vrManager.makeSystemTip(dir)
    if (this.touchpad) {
      if (this.touchpadState) {
        this.touchpadUp();
      }
      if (this.deviceType === DeviceType.PICO) {
        switch (dir) {
          case TouchpadDir.LEFT:
            this.touchpad.rotateZ(-(20 * Math.PI) / 180);
            break;
          case TouchpadDir.RIGHT:
            this.touchpad.rotateZ((20 * Math.PI) / 180);
            break;
          case TouchpadDir.UP:
            this.touchpad.rotateX((20 * Math.PI) / 180);
            break;
          case TouchpadDir.DOWN:
            this.touchpad.rotateX(-(20 * Math.PI) / 180);
            break;
        }
      } else if (this.deviceType === DeviceType.PICO4) {
        switch (dir) {
          case TouchpadDir.LEFT:
            if (this.handness === Handness.LEFT) {
              this.touchpad.rotateZ((20 * Math.PI) / 180);
            } else {
              this.touchpad.rotateZ(-(20 * Math.PI) / 180);
            }
            break;
          case TouchpadDir.RIGHT:
            if (this.handness === Handness.LEFT) {
              this.touchpad.rotateZ(-(20 * Math.PI) / 180);
            } else {
              this.touchpad.rotateZ((20 * Math.PI) / 180);
            }
            break;
          case TouchpadDir.UP:
            this.touchpad.rotateX((20 * Math.PI) / 180);
            break;
          case TouchpadDir.DOWN:
            this.touchpad.rotateX(-(20 * Math.PI) / 180);
            break;
        }
      } else if (this.deviceType === DeviceType.HTC) {
        switch (dir) {
          case TouchpadDir.LEFT:
            //this.touchpad.position.set(0, 0.0198, 0.008134)
            this.touchpad.rotation.copy(
              new Euler(
                (6.686 * Math.PI) / 180,
                (0.62 * Math.PI) / 180,
                (8 * Math.PI) / 180
              )
            );
            break;
          case TouchpadDir.RIGHT:
            //this.touchpad.position.set(0, 0.0198, 0.008134)
            this.touchpad.rotation.copy(
              new Euler(
                (6.686 * Math.PI) / 180,
                (-0.53 * Math.PI) / 180,
                (-6.771 * Math.PI) / 180
              )
            );
            break;
          case TouchpadDir.UP:
            //this.touchpad.position.set(0, 0.0198, 0.008134)
            this.touchpad.rotation.copy(new Euler((16 * Math.PI) / 180, 0, 0));
            break;
          case TouchpadDir.DOWN:
            //this.touchpad.position.set(0, 0.0198, 0.008134)
            this.touchpad.rotation.copy(
              new Euler((-4.64 * Math.PI) / 180, 0, 0)
            );
            break;
        }
      }
      this.touchpadState = true;
    }
  }

  /**
   * 圆盘或摇杆松开
   */
  touchpadUp() {
    if (this.touchpad && this.touchpadState) {
      if (
        this.deviceType === DeviceType.PICO ||
        this.deviceType === DeviceType.PICO4
      ) {
        this.touchpad.rotation.copy(new Euler(0, 0, 0));
      } else if (this.deviceType === DeviceType.HTC) {
        //this.touchpad.position.set(0, 0.01987127, -0.008134438)
        //this.touchpad.rotation.copy(new Euler((-6.686 * Math.PI) / 180, 0, 0))
        this.touchpad.position.copy(this.touchpadPos);
        this.touchpad.rotation.copy(this.touchpadRot);
      }

      this.touchpadState = false;
    }
  }
}
